
// Shared "copy coordinates to clipboard" behavior for every digitization
// label's copy-icon (see FeatureVisual.js's createCopyIconMarker()/
// _wireCopyIcon()) - one clipboard helper, one toast, and the guard flag
// that stops a copy-icon click from also placing a new digitization vertex
// at the same screen position.
//
// Why the guard is enough: a copy-icon click is detected via Potree's
// InputHandler (3D raycast picking -> synthetic 'click' event), which is
// dispatched synchronously from InputHandler's own native 'mouseup' listener
// on the canvas. That listener was registered once, early, when the viewer
// was constructed - long before any digitization tool's own native
// 'mouseup' listener (added only once a tool is actively capturing). DOM
// listeners for the same event on the same element fire in registration
// order, so InputHandler's handler - and therefore this guard being set -
// always completes before a tool's own placement handler runs on that same
// click. Every such handler checks/resets this flag before placing a vertex.
import {ensureHudStyle, registerHudSlot, layoutHudStack} from "./HudSkin.js";
import {t} from "./i18n/index.js";
export const copyClickGuard = {consumed: false};

// Exported for Level 1 unit testing (see CopyFeedback.test.js) - also the
// one formatter every "copy coordinates" affordance in Digitalisering goes
// through (label copy-icons via copyCoordinatesToClipboard() below, and the
// coordinate infobox - see CoordinateInfoBox.js), so its precision (3
// decimals, no thousands separators) never drifts between them.
export function formatCoordinatesForClipboard(position){
	return `X: ${position.x.toFixed(3)}\nY: ${position.y.toFixed(3)}\nZ: ${position.z.toFixed(3)}`;
}

let toastEl = null;
let toastTimer = null;

// Exported (task: "diskret feedback" for the connection-point workflow, see
// DigitizationModule.js's checkVertexMoveConnectionWarning()) - the same
// top-center, non-blocking, auto-dismissing affordance every "copy"
// confirmation in this module already uses, rather than a second toast
// implementation. `durationMs` defaults to the copy-toast's own original
// fixed 1400ms; callers with a longer message (e.g. a broken-connection
// notice, which names the affected feature(s)) can ask for more read time.
//
// Top-center HUD (fælles skin, HudSkin.js): placeres under tool-instruktionen, hvis den er synlig (den kan ombrydes til flere
// linjer), ellers øverst i viewer-området - så de to aldrig overlapper.
export function showToast(message, durationMs = 1400){
	if(!toastEl){
		ensureHudStyle();
		toastEl = document.createElement('div');
		toastEl.className = 'pvh pvh--top';
		toastEl.style.cssText = 'font-weight: bold; z-index: 10001; display: none;';
		document.body.appendChild(toastEl);
		registerHudSlot('toast', toastEl);
	}

	toastEl.textContent = message;
	toastEl.style.display = 'block';
	layoutHudStack(); // under en synlig instruktion; en synlig statusplads lægger sig under toasten
	clearTimeout(toastTimer);
	toastTimer = setTimeout(() => { toastEl.style.display = 'none'; layoutHudStack(); }, durationMs);
}

// Copies a point's current X/Y/Z (full precision at write time, formatted as
// plain text so it pastes directly into Notepad/Word/Excel/Teams/etc.) and
// shows a brief, dialog-free confirmation. Never mutates the point itself -
// this only ever reads position.x/y/z.
export function copyCoordinatesToClipboard(position){
	copyTextToClipboard(formatCoordinatesForClipboard(position), t('Koordinater kopieret'), t('Kunne ikke kopiere koordinater'));
}

// Copies arbitrary plain text (e.g. a feature's full result label, for tools
// like Pipe where "copy" should hand over the computed measurements rather
// than a raw coordinate - see PipeVisual.js) with the same guard/toast
// behavior as copyCoordinatesToClipboard().
export function copyTextToClipboard(text, successMessage = t('Kopieret'), failMessage = t('Kunne ikke kopiere')){
	copyClickGuard.consumed = true;

	navigator.clipboard.writeText(text)
		.then(() => showToast(successMessage))
		.catch(() => showToast(failMessage));
}
